import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { classNames } from '~/utils/classNames';

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  component?: React.ReactNode;
}

export interface DropdownProps {
  id?: string;
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
}

const dropdownVariants = {
  hidden: {
    opacity: 0,
    y: -4,
    scale: 0.97,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.15,
      ease: [0.4, 0, 0.2, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -4,
    scale: 0.97,
    transition: {
      duration: 0.1,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    { id, options, value, onChange, className, placeholder = 'Select an option', disabled = false, label, size = 'md' },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selectedOption = options.find((option) => option.value === value);

    const sizeClasses = {
      sm: 'px-2.5 py-1.5 text-xs',
      md: 'px-3 py-2 text-[13px]',
      lg: 'px-4 py-2.5 text-sm',
    };

    const itemSizeClasses = {
      sm: 'px-2.5 py-1.5 text-xs',
      md: 'px-3 py-2 text-[13px]',
      lg: 'px-4 py-2.5 text-sm',
    };

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    return (
      <div ref={dropdownRef} className={classNames('relative', className)}>
        {label && (
          <label className="block text-[11px] font-medium text-[var(--dualite-text-tertiary)] uppercase tracking-wider mb-1.5">
            {label}
          </label>
        )}
        <div
          className={classNames(
            'flex items-center justify-between rounded-md border transition-all duration-200 select-none',
            sizeClasses[size],
            'bg-[var(--dualite-background-button)] border-[var(--dualite-border-secondary)] text-[var(--dualite-text-primary)]',
            disabled
              ? 'opacity-40 cursor-not-allowed'
              : isOpen
                ? 'cursor-pointer border-[var(--dualite-border-secondary)] bg-[var(--dualite-background-button-hover)]'
                : 'cursor-pointer hover:border-[var(--dualite-border-secondary)] hover:bg-[var(--dualite-background-button-hover)]',
          )}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          role="button"
          tabIndex={disabled ? -1 : 0}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              setIsOpen(!isOpen);
            }

            if (e.key === 'Escape') {
              setIsOpen(false);
            }
          }}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          id={id}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {selectedOption?.icon && (
              <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center opacity-80">
                {selectedOption.icon}
              </span>
            )}
            <span
              className={classNames(
                'truncate leading-normal',
                !selectedOption ? 'text-[var(--dualite-text-tertiary)]' : 'text-[var(--dualite-text-primary)]',
              )}
            >
              {selectedOption?.label || placeholder}
            </span>
          </div>
          <motion.div
            animate={{ rotate: isOpen ? 180 : 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="flex-shrink-0 ml-2 text-[var(--dualite-text-tertiary)]"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3 5L7 9L11 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={ref}
              className={classNames(
                'absolute z-50 w-full mt-1 rounded-md border shadow-xl overflow-hidden',
                'border-[var(--dualite-border-secondary)] bg-[var(--dualite-background-tertiary)]',
              )}
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <ul
                className="py-1 max-h-56 overflow-auto scrollbar-thin scrollbar-thumb-[var(--dualite-scrollbar-thumb)] scrollbar-track-transparent"
                role="listbox"
                aria-labelledby={id}
                aria-activedescendant={selectedOption?.value}
              >
                {options.map((option) => (
                  <li
                    key={option.value}
                    className={classNames(
                      'flex items-center gap-2 transition-colors duration-100',
                      itemSizeClasses[size],
                      option.disabled
                        ? 'opacity-30 cursor-not-allowed text-[var(--dualite-text-disabled)]'
                        : option.value === value
                          ? 'bg-[var(--dualite-background-button-hover)] text-[var(--dualite-text-primary)] cursor-default'
                          : 'text-[var(--dualite-text-secondary)] hover:bg-[var(--dualite-background-button)] hover:text-[var(--dualite-text-primary)] cursor-pointer',
                    )}
                    role="option"
                    aria-selected={option.value === value}
                    onClick={() => {
                      if (!option.disabled) {
                        onChange(option.value);
                        setIsOpen(false);
                      }
                    }}
                  >
                    {option.icon && (
                      <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center opacity-75">
                        {option.icon}
                      </span>
                    )}
                    <span className="truncate leading-normal flex-1">{option.label}</span>
                    {option.value === value && (
                      <span className="flex-shrink-0 ml-auto text-[var(--dualite-text-tertiary)]">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M2 6L5 9L10 3"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';
